import type { DueEntryT } from "@patient-watch/types";

import type { FormsToDoItem } from "../features/new/hooks/useFormsToDo";

type DueEntry = NonNullable<DueEntryT>;

function dueAtTimestamp(entry: DueEntry): number {
    return new Date(entry.due_at).getTime();
}

/** Due entries the patient can start now (unlocked schedule slots). */
export function isActionableDueEntry(entry: DueEntryT | null | undefined): entry is DueEntry {
    return entry != null && Boolean(entry.due_at) && entry.is_locked !== true;
}

export function findEarliestDueEntry(entries: (DueEntryT | null | undefined)[] | null | undefined): DueEntry | null {
    const valid = (entries ?? []).filter(isActionableDueEntry);

    if (!valid.length) {
        return null;
    }

    return valid.reduce((earliest, current) =>
        dueAtTimestamp(current) < dueAtTimestamp(earliest) ? current : earliest
    );
}

export function resolveTrackerDueEntry(tracker: {
    due_entries?: (DueEntryT | null)[] | null;
    next_entry?: DueEntryT | null;
}): DueEntry | null {
    return findEarliestDueEntry(tracker.due_entries) ?? tracker.next_entry ?? null;
}

/** Prefer unlocked due_entries, then an unlocked next_entry fallback. */
export function resolveActionableTrackerDueEntry(tracker: {
    due_entries?: (DueEntryT | null)[] | null;
    next_entry?: DueEntryT | null;
}): DueEntry | null {
    const fromDueEntries = findEarliestDueEntry(tracker.due_entries);
    if (fromDueEntries) return fromDueEntries;
    return isActionableDueEntry(tracker.next_entry) ? tracker.next_entry : null;
}

export interface EarliestDueFormItem {
    item: FormsToDoItem;
    dueEntry: DueEntry;
}

/**
 * The form the patient should do next, from the forms they can start now. The form's tracker `sort_order`
 * decides, so this matches the order of the To Do list. Forms with the same `sort_order` go by earliest
 * due date, then name.
 */
export function findEarliestDueFormItem(items: FormsToDoItem[]): EarliestDueFormItem | null {
    const candidates = items.flatMap((item) => {
        const dueEntry = resolveActionableTrackerDueEntry(item.data);
        return dueEntry ? [{ item, dueEntry }] : [];
    });

    candidates.sort(
        (a, b) =>
            (a.item.data.sort_order ?? 0) - (b.item.data.sort_order ?? 0) ||
            dueAtTimestamp(a.dueEntry) - dueAtTimestamp(b.dueEntry) ||
            a.item.name.localeCompare(b.item.name)
    );

    return candidates[0] ?? null;
}
